import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import CustomTextField from "../../common/CustomTextField";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import CustomDatePicker from "../../common/CustomDatePicker";
import { Controller, useForm } from "react-hook-form";
import { FileUploadOutlined, Cancel } from "@mui/icons-material";
import Button from "../../common/Button";
import { tokens } from "../../../theme";
import { ProductDocument, updateElementInProductSlice, uploadProductDocument, useProductReducer } from "../../../redux/slice/product/createProductSlice";
import { EXTERNAL_LINK_TYPE, PRODUCT_DOCUMENT_TYPES } from "../../../constants/applicationConstants";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import dayjs from "dayjs";
import { pdf, doc, docx, jpeg, png, xls, xlsx, mov, mp4, avi, amv, asf, dv, f4v, flv, m4v, mkv, mxf, ogv, rm, rmvb, ts, video3gp, vob, webm, wmv, video3g2, PRODUCT_DOCUMENT_STATUS } from "../../../constants/applicationConstants";
import { ProductInfo, useProductListReducer } from "../../../redux/slice/product/productListSlice";
import { sortNames } from "../../../utils/Utility";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useUserReducer } from "../../../redux/slice/userSlice";
import { fetchAppConfigList, fetchDocumentByIdCommonApi, ManufacturerInfo, setSingleAlertObj, SingleAlertInfo, useCommonReducer } from "../../../redux/slice/commonSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { getProductDocumentById, useDocumentReducer } from "../../../redux/slice/document/DocumentSlice";
import CustomStyledRadio from "../ivr/CustomStyledRadio";
import { useModalStyles } from "../../common/useModalStyles";
import { error } from "console";


interface DocumentUploadPopupProps {
  open: boolean;
  onClose: () => void;
  productDocumentId: number | null
}

interface Attachment {
  documentName: string;
  content: string;
  fileType: string;
}

type Option = {
  value: string | number,
  label: string
};

const statusUpdateDocumentTypes = [
  PRODUCT_DOCUMENT_TYPES.IVR_FORM,
  PRODUCT_DOCUMENT_TYPES.ORDER_FORM,
  PRODUCT_DOCUMENT_TYPES.SERVICE_AGREEMENT,
  PRODUCT_DOCUMENT_TYPES.TISSUE_TRACKING_FORM,
  PRODUCT_DOCUMENT_TYPES.RETURN_FORM
];


const DocumentUploadPopup: React.FC<DocumentUploadPopupProps> = ({
  open,
  onClose,
  productDocumentId
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<ProductDocument>({
    defaultValues: {
      attachmentProductDocument: [],
    },
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const {
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    iconBoxSx,
    closeIconSx,
    dropdownWrapSx,
    datepickerWrapSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
    tokens: modalTokens,
  } = useModalStyles();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const { productsByUserRole } = useProductListReducer();
  const { manufacturerInfoListUserSlice } = useUserReducer();
  const { uploadProductDocumentStatus } = useProductReducer();
  const { productDocumentById } = useDocumentReducer();
  const { documentDataFromCommonApi, appConfigList } = useCommonReducer();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeRow, setActiveRow] = useState<string | null>(null);
  const [productDocumentList, setProductDocumentList] = React.useState<CodeTable[]>([]);

  const [selectedProductOption, setSelectedProductOption] = useState<string | null>();
  const [selectedManufacturerOption, setSelectedManufacturerOption] = useState<string | null>();
  const [selectedCategoryOption, setSelectedCategoryOption] = useState<string | null>();
  const [selectedSubCategoryOption, setSelectedSubCategoryOption] = useState<string | null>();
  const [ categoryList, setCategoryList] = useState<CodeTable[]>([]);
  const [ subCategoryList, setSubCategoryList] = useState<CodeTable[]>([]);
  const [uploadedInitialDocument, setUploadedInitialDocument] = useState<ExtendedFile[]>([]);
  const [ checkPublish, setCheckPublish ] = useState(false);
  const [ mfcOrProdError, setMfcOrProdError ] = useState(false);
  const [ mfcError, setMfcError ] = useState(false);
  const [ categoryError, setCategoryError ] = useState(false);
  const [ productCategories, setProductCategories ] = useState<Map<string, string[]>>(new Map());
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setActiveRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveRow(null);
  };

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: modalTokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: modalTokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  //mount
  useEffect(() => {
    dispatch(fetchAppConfigList());
  }, [])

  useEffect(() => {
    if(documentDataFromCommonApi && productDocumentById) {
      setInitialDocument();
    }
  }, [documentDataFromCommonApi, productDocumentById])

  const setInitialDocument = async () => {
    const { documentId, documentName, type, size } = documentDataFromCommonApi;
  
    const documentsMetaData = [{
      documentId: documentId ? documentId.toString() : "",
      name: documentName ? documentName : "",
      type: type || "Unknown",
      size: size ? size : 0,
      timestamp: '',
      lastModified: 0,
      webkitRelativePath: "",
      arrayBuffer: async () => new ArrayBuffer(0),
      bytes: async () => new Uint8Array(0),
      slice: () => new Blob(),
      stream: () => new ReadableStream(),
      text: async () => "",
    }]

    const attachments: Attachment[] =  [{
      documentName: documentName,
      content: documentDataFromCommonApi.content,
      fileType: type,
    }]
    setValue("attachmentProductDocument", attachments);
    setUploadedInitialDocument(documentsMetaData);
  };

  const defaultValues = useCallback(() => {
    if(productDocumentById) {
      reset({
        productDocumentId: productDocumentById.productDocumentId,
        documentId: productDocumentById.documentId,
        documentType : productDocumentById.documentType,
        name: productDocumentById.documentName,
        comments: (productDocumentById.comments)?.toString(),
        effectiveDate: productDocumentById.effectiveDate,
        productId: productDocumentById.productId,
        manufactureId: productDocumentById.manufacturerId,
        productDocumentInfo: { documentURL: productDocumentById?.productDocumentInfo?.documentURL },
        category: productDocumentById.category,
        subCategory: productDocumentById.subCategory,
        editProductDocument: true,
        publish: !!productDocumentById.publish,
        externalLinkType: productDocumentById.externalLinkType
      });
      setSelectedProductOption(productDocumentById.productName);
      setSelectedManufacturerOption(productDocumentById.manufacturerName);
      setSelectedCategoryOption(productDocumentById.category);
      setSelectedSubCategoryOption(productDocumentById.subCategory);
      setCheckPublish(!!productDocumentById.publish);
      if(productDocumentById.externalLinkType === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT && productDocumentById.documentId) {
        dispatch(fetchDocumentByIdCommonApi(productDocumentById.documentId));
      }
    } else{
      reset();
    }
  }, [productDocumentById]);

  useEffect(() => {
    defaultValues();
  }, [defaultValues])

  useEffect(() => {
    if(productDocumentId) {
      dispatch(getProductDocumentById(productDocumentId))
    }
  }, [productDocumentId]);

  const handleOptionSelect = (row: string, option: Option) => {
    const updateSelection = (
      currentSelections: string[],
      setSelections: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
      const isSelected = currentSelections.includes(option.label);
      const updatedSelections = isSelected
        ? currentSelections.filter((item) => item !== option.label)
        : [...currentSelections, option.label];
      setSelections(updatedSelections);
      handleMenuClose();
    };

    switch (row) {
      case "product":
        setSelectedProductOption(option.label)
        setValue('productId', parseInt((option.value).toString()));
        setValue('manufactureId', null);
        setSelectedManufacturerOption(null);
        break;
      case "manufacturer":
        setSelectedManufacturerOption(option.label);
        setValue('manufactureId', parseInt((option.value).toString()));
        setValue('productId', null);
        setSelectedProductOption(null);
        break;
      case "category":
        setSelectedCategoryOption(option.label)
        setValue('category', (option.value).toString());
        setValue('subCategory', null);
        setSelectedSubCategoryOption(null);
        break;
      case "subCategory":

        setSelectedSubCategoryOption(option.label)
        setValue('subCategory', (option.value).toString());
        break;
      default:
        break;
    }
    handleMenuClose();
  };
  
  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedDocumentList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductDocument"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setProductDocumentList(sortedDocumentList);

      const sortedCategoryList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductCategory"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setCategoryList(sortedCategoryList);

      const sortedSubCategoryList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductSubCategory"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setSubCategoryList(sortedSubCategoryList);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const productDocumentOptions = useMemo(() => {
    return productDocumentList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [productDocumentList]);

  const categoryListOptions = useMemo(() => {
    return categoryList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [categoryList]);

  const subCategoryListOptions = useMemo(() => {
    if(selectedCategoryOption) {
      const selectedSubCategoryList = productCategories.get(selectedCategoryOption)
      return selectedSubCategoryList?.map((item: string) => ({
        label: item,
        value: item
      }));
    }
  }, [selectedCategoryOption]);

  useEffect(() => {
    if(!appConfigList?.length) return;
      const productCategoryConfig = appConfigList.find(config => config.configName === 'product.document.category.sequence');
      if(productCategoryConfig) {
        const productCategoryConfigValue = JSON.parse(productCategoryConfig.configValue);
        const productCategoriesObj = productCategoryConfigValue.categoryAndSubCategoryList || {};
        const typedEntries = Object.entries(productCategoriesObj).map(
          ([key, value]): [string, string[]] => {
            if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
              return [key, value as string[]];
            }
            return [key, []];
          }
        );
        setProductCategories(new Map(typedEntries))
    }
  }, [appConfigList]);


  const handleEffectiveDate = (date: Date | null) => {
    if (dayjs(date).isValid()) {
      setValue("effectiveDate", dayjs(date).format("MM/DD/YYYY"));
    };
  };

  const handleDropzoneChange = async (uploadedFiles: ExtendedFile[]) => {
    setValue('documentId', null);
    const readFileAsBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    };
    const attachments: Attachment[] = await Promise.all(
      uploadedFiles.map(async (file) => {
        const base64Content = await readFileAsBase64(file);
        return {
          documentName: file.name,
          content: base64Content.split(",")[1],
          fileType: file.type,
        };
      })
    );
    const documentName = attachments[0]?.documentName || "";
    const name = documentName?.substring(0, documentName.lastIndexOf('.')) || documentName;
    setValue('name', name);
    setValue("attachmentProductDocument", attachments);
  };

  const isDropzoneDisabled =
    Boolean(watch('productDocumentInfo.documentURL')) &&
    watch('documentType') === 'PAYMENT INSTRUCTION';

  const productOptions = useMemo(() => {
    let options: Option[] = [];
    if (productsByUserRole && productsByUserRole.length > 0) {
      if (productsByUserRole.length === 1) {
        setValue('productId', Number(productsByUserRole[0]?.productId));
      }
      const sortedProductList: ProductInfo[] = sortNames(productsByUserRole, 'productName');
      sortedProductList?.map((option) => {
        options.push({ label: option.productName, value: option.productId })
      });
    };
    return options;
  }, [productsByUserRole]);

  const manufacturersList = useMemo(() => {
    let options: Option[] = [];
    if (manufacturerInfoListUserSlice && manufacturerInfoListUserSlice.length > 0) {
      const sortedInfo: ManufacturerInfo[] = sortNames(manufacturerInfoListUserSlice, 'manufacturerName');
      sortedInfo?.map(option => {
        options.push({ label: option.manufacturerName, value: option.manufacturerId });
      });
    }
    return options;
  }, [manufacturerInfoListUserSlice]);

  const onSubmit = async (data: ProductDocument) => {
    if (statusUpdateDocumentTypes.includes(data?.documentType)) {
      data.status = PRODUCT_DOCUMENT_STATUS.PENDING;
    }
    const response = await dispatch(uploadProductDocument({ productDocument: data }));
  };
  useEffect(() => {
    if (uploadProductDocumentStatus === 'success') {
      let successObj: SingleAlertInfo = {
        message: "Product document(s) saved successfully.",
        alertType: "success",
      };
      dispatch(setSingleAlertObj(successObj));
      dispatch(updateElementInProductSlice({
        elementName: "uploadProductDocumentStatus",
        value: 'idle'
      }));
      navigate("/resource_list", { state: { withSearchHistory: true } });
      onClose();
    }
  }, [uploadProductDocumentStatus]);

  const externalLinkTypeOptions = [
    { value: EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT, label: EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT },
    { value: EXTERNAL_LINK_TYPE.EXTERNAL_URL, label: EXTERNAL_LINK_TYPE.EXTERNAL_URL},
  ];

  const clearValuesUponTypeChange = () => {
    setValue('attachmentProductDocument', []);
    setValue('productDocumentInfo.documentURL', '');
    setValue('documentType', '');
    setValue('documentId', null);
    setUploadedInitialDocument([]);
    setValue('name', '');
  } 

  const handleExternalLinkTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e?.target;
    value && setValue('externalLinkType', value);
    clearValuesUponTypeChange();
  }

  const clearTagErrors = () => {
    clearErrors('manufacturer');
    clearErrors('product');
    clearErrors('category');
    setMfcOrProdError(false);
    setCategoryError(false);
    setMfcError(false);
  };

  const scrollToTop = () => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const onError = (_error: any, e: any) => {
    let hasError = false;
    clearTagErrors();

    if(!getValues('effectiveDate')) {
      setError("effectiveDate", { type: "required", message: "Effective Date is required" });
      hasError = true;
      scrollToTop();
    } else if(!getValues('name')) {
      setError("name", { type: "required", message: watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT ? "Document Name is required" : "External URL Name is required" });
      hasError = true;
    };
    
    // if(getValues('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT) {
    // if(!getValues('manufactureId') && !getValues('productId')) {
    //   setError("manufacturer", { type: "required", message: "Manufacturer or Product is required" });
    //   setMfcOrProdError(true);
    //   hasError = true;
    // };
    // }

    // If documentType is Payment Instruction, manufacturer is required
    if (getValues('documentType') === 'PAYMENT INSTRUCTION' && !getValues('manufactureId')) {
      setError("manufacturer", { type: "required", message: "Manufacturer is required for Payment Instruction" });
      setMfcError(true);
      hasError = true;
    } 

    if(!getValues('category')) {
      setError('category', { type: "required", message: "Category is required" });
      setCategoryError(true);
      hasError = true;
    };

    if(getValues('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT && watch('documentType') !== 'PAYMENT INSTRUCTION') {
      clearErrors('attachmentProductDocument');
      clearErrors('productDocumentInfo.documentURL');
      if(getValues('attachmentProductDocument')?.length == 0 && !getValues('productDocumentInfo.documentURL')) {
        setError("attachmentProductDocument", { type: "required", message: "Product document or Document URL is required" });
        setError("productDocumentInfo.documentURL", { type: "required", message: "Product document or Document URL is required" });
        hasError = true;
        scrollToTop();
      };
    } else if(getValues('externalLinkType') === EXTERNAL_LINK_TYPE.EXTERNAL_URL) {
      clearErrors('productDocumentInfo.documentURL');
      if(!getValues('productDocumentInfo.documentURL')) {
        setError("productDocumentInfo.documentURL", { type: "required", message: "External URL is required" });
        hasError = true;
      };
    } else if (getValues('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT && getValues('documentType') === 'PAYMENT INSTRUCTION') {
      // CS-1651 start
      if (!getValues('productDocumentInfo.documentURL') && getValues('attachmentProductDocument')?.length > 0) {
        clearErrors('productDocumentInfo.documentURL');
      } else if (getValues('productDocumentInfo.documentURL') && !getValues('attachmentProductDocument')?.length) {
        clearErrors('attachmentProductDocument');
      } else if (!getValues('productDocumentInfo.documentURL') && !getValues('attachmentProductDocument')?.length) {
        setError("attachmentProductDocument", { type: "required", message: "Product document or Document URL is required" });
        setError("productDocumentInfo.documentURL", { type: "required", message: "Product document or Document URL is required" });
        hasError = true;
      }
      // CS-1651 end
    }
    if(!hasError) {
      onSubmit(getValues());
    };
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(modalTokens.accent)}>
          <img
            src={require("../../../assets/upload_document.png")}
            alt="Resources"
            style={{ width: 20, height: 20 }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: modalTokens.textPrimary }}>
            Upload Resources
          </Typography>
          <Typography variant="body2" sx={{ color: modalTokens.textSecondary, mt: 0.5 }}>
            Upload your Resource here. Supported formats: PDF, DOC, DOCX, JPEG, PNG, XLS, XLSX, MP4, MOV. Maximum file size: 50MB.
          </Typography>
        </Box>

        <IconButton aria-label="close" onClick={onClose} sx={closeIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent ref={dialogContentRef} sx={bodySx}>
        <form id="document-upload-form" onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <CustomStyledRadio
                controllerName={`externalLinkType`}
                control={control}
                options={externalLinkTypeOptions}
                onChange={handleExternalLinkTypeChange}
                defaultValue="Product Document"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FieldLabel label="Resource Type" required />
              <Box sx={dropdownWrapSx(!!errors.documentType)}>
                <CustomDropdownField
                  name="documentType"
                  control={control}
                  options={productDocumentOptions}
                  label=""
                  rules={{
                    validate: (value: any) => {
                      const externalLinkType = watch('externalLinkType');
                      if (externalLinkType === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT && !value) {
                        scrollToTop();
                        return "Resource type is required.";
                      }
                      return true;
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid item md={6} xs={12}>
              <FieldLabel label="Effective Date" required />
              <Box sx={datepickerWrapSx(!!errors.effectiveDate)}>
                <CustomDatePicker
                  name="effectiveDate"
                  controllerName="effectiveDate"
                  control={control}
                  label=""
                  onChange={handleEffectiveDate}
                  rules={{ required: "Effective Date is required." }}
                />
              </Box>
            </Grid>
            {watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT && (
              <Grid item xs={12}>
                <Dropzone
                  isDisabled={isDropzoneDisabled}
                  name="attachmentProductDocument"
                  label=""
                  onFileChange={() => { }}
                  onFileChangeTest={handleDropzoneChange}
                  control={control}
                  maxFileCount={1}
                  maxFileSize={{ size: 50, unit: "MB" }}
                  allowedFileTypes={{
                    ...pdf,
                    ...doc,
                    ...docx,
                    ...jpeg,
                    ...png,
                    ...xls,
                    ...xlsx,
                    ...mov,
                    ...mp4,
                    ...avi,
                    ...flv,
                    ...ogv,
                    ...webm,
                    ...ts,
                    ...mkv,
                    ...wmv,
                    ...video3gp,
                    ...video3g2,
                    ...m4v,
                    ...asf,
                    ...f4v,
                    ...rm,
                    ...rmvb,
                    ...mxf,
                    ...vob,
                    ...dv,
                    ...amv,
                  }}
                  initialFilesMetaData={uploadedInitialDocument}
                  rules={
                    watch('documentType') === 'PAYMENT INSTRUCTION' &&
                    watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT &&
                    !watch('productDocumentInfo.documentURL')
                      ? { required: "Product document or Document URL is required" }
                      : undefined
                  }
                />
              </Grid>
            )}
            {watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT && (
              <Grid item md={12} xs={12} textAlign="center">
                <Typography variant="h6">OR</Typography>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FieldLabel
                label={
                  watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT
                    ? "Document Name"
                    : "External URL Name"
                }
                required
              />
              <Box sx={inputFieldSx(!!errors.name)}>
                <CustomTextField
                  control={control}
                  controllerName="name"
                  label=""
                  rules={{
                    required:
                      watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT
                        ? "Document Name is required."
                        : "External URL Name is required.",
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel
                label={
                  watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT
                    ? "Document URL"
                    : "External URL"
                }
                required={
                  watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT &&
                  (watch('documentType') !== 'PAYMENT INSTRUCTION' ||
                    (watch('documentType') === 'PAYMENT INSTRUCTION' &&
                      !(watch('attachmentProductDocument')?.length > 0)))
                }
              />
              <Box sx={inputFieldSx(!!errors.productDocumentInfo?.documentURL)}>
                <CustomTextField
                  disabled={
                    watch('documentType') === 'PAYMENT INSTRUCTION' &&
                    watch('attachmentProductDocument')?.length > 0
                  }
                  controllerName="productDocumentInfo.documentURL"
                  control={control}
                  rules={
                    watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT &&
                    watch('documentType') !== 'PAYMENT INSTRUCTION'
                      ? { required: "Document URL is required." }
                      : watch('externalLinkType') === EXTERNAL_LINK_TYPE.EXTERNAL_URL
                      ? { required: "External URL is required." }
                      : watch('externalLinkType') === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT &&
                        watch('documentType') === 'PAYMENT INSTRUCTION' &&
                        !(watch('attachmentProductDocument')?.length > 0)
                      ? { required: "Document URL is required." }
                      : {}
                  }
                  label=""
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FieldLabel label="Comments" />
              <Box sx={inputFieldSx(false)}>
                <CustomTextField
                  control={control}
                  controllerName="comments"
                  label=""
                  multiline
                  rows={3}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography>Tags</Typography>
              <TableContainer
                component={Paper}
                sx={{
                  border: 1,
                  borderRadius: "8px",
                  borderColor: colors.grey[600],
                }}
              >
                <Table size="small">
                  <TableBody>
                    <Controller
                      name="manufacturer"
                      control={control}
                      rules={{
                        validate: (value: any) => {
                          const documentType = watch('documentType');
                          const manufactureId = getValues('manufactureId');
                          const productId = getValues('productId');

                          if (documentType === 'PAYMENT INSTRUCTION' && !manufactureId) {
                            return "Manufacturer is required for Payment Instruction.";
                          }
                          if (!manufactureId && !productId) {
                            return "Manufacturer or Product is required.";
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => {
                        return (
                          <TableRow>
                            <TableCell
                              sx={{
                                width: "30%",
                                color:
                                  mfcOrProdError || mfcError
                                    ? theme.palette.error.main
                                    : '',
                              }}
                            >
                              Manufacturer
                            </TableCell>
                            <TableCell sx={{ width: "70%" }}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="text"
                                  onClick={(e) => handleMenuOpen(e, "manufacturer")}
                                  removeUpperCase={true}
                                  fullWidth
                                  sx={{
                                    color:
                                      mfcOrProdError || mfcError
                                        ? theme.palette.error.main
                                        : '',
                                  }}
                                >
                                  {selectedManufacturerOption || "-"}
                                </Button>
                                {selectedManufacturerOption && (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedManufacturerOption(null);
                                      setValue('manufactureId', null);
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              <Menu
                                anchorEl={menuAnchor}
                                open={
                                  Boolean(menuAnchor) &&
                                  activeRow === "manufacturer"
                                }
                                onClose={handleMenuClose}
                              >
                                {manufacturersList.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    onClick={() =>
                                      handleOptionSelect("manufacturer", option)
                                    }
                                  >
                                    <ListItemIcon>
                                      <FolderIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={option?.label} />
                                  </MenuItem>
                                ))}
                              </Menu>
                            </TableCell>
                          </TableRow>
                        );
                      }}
                    />

                    <Controller
                      control={control}
                      name={'product'}
                      rules={
                        !getValues('manufactureId') && !getValues('productId')
                          ? { required: "Manufacturer or Product is required." }
                          : {}
                      }
                      render={({ field, fieldState }) => {
                        return (
                          <TableRow>
                            <TableCell
                              sx={{
                                width: "30%",
                                color: mfcOrProdError
                                  ? theme.palette.error.main
                                  : '',
                              }}
                            >
                              Product
                            </TableCell>
                            <TableCell sx={{ width: "70%" }}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="text"
                                  onClick={(e) => handleMenuOpen(e, "product")}
                                  removeUpperCase={true}
                                  fullWidth
                                  sx={{
                                    color: mfcOrProdError
                                      ? theme.palette.error.main
                                      : '',
                                  }}
                                >
                                  {selectedProductOption || "-"}
                                </Button>
                                {selectedProductOption && (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedProductOption(null);
                                      setValue('productId', null);
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              <Menu
                                anchorEl={menuAnchor}
                                open={
                                  Boolean(menuAnchor) && activeRow === "product"
                                }
                                onClose={handleMenuClose}
                              >
                                {productOptions.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    onClick={() =>
                                      handleOptionSelect("product", option)
                                    }
                                  >
                                    <ListItemIcon>
                                      <FolderIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={option?.label} />
                                  </MenuItem>
                                ))}
                              </Menu>
                            </TableCell>
                          </TableRow>
                        );
                      }}
                    />

                    <Controller
                      control={control}
                      name={'category'}
                      rules={
                        getValues('category')
                          ? { required: "Category is required." }
                          : {}
                      }
                      render={({ field, fieldState }) => {
                        return (
                          <TableRow>
                            <TableCell
                              sx={{
                                width: "30%",
                                color: categoryError
                                  ? theme.palette.error.main
                                  : '',
                              }}
                            >
                              Category
                            </TableCell>
                            <TableCell sx={{ width: "70%" }}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="text"
                                  onClick={(e) => handleMenuOpen(e, "category")}
                                  removeUpperCase={true}
                                  fullWidth
                                  sx={{
                                    color: fieldState.error
                                      ? theme.palette.error.main
                                      : '',
                                  }}
                                >
                                  {selectedCategoryOption || "-"}
                                </Button>
                                {selectedCategoryOption && (
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedCategoryOption(null);
                                      setValue('category', null);
                                      setSelectedSubCategoryOption(null);
                                      setValue('subCategory', null);
                                    }}
                                    sx={{ ml: 1 }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              <Menu
                                anchorEl={menuAnchor}
                                open={
                                  Boolean(menuAnchor) && activeRow === "category"
                                }
                                onClose={handleMenuClose}
                              >
                                {categoryListOptions.map((option) => (
                                  <MenuItem
                                    key={option?.value}
                                    onClick={() => {
                                      handleOptionSelect("category", option);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <FolderIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={option?.label} />
                                  </MenuItem>
                                ))}
                              </Menu>
                            </TableCell>
                          </TableRow>
                        );
                      }}
                    />

                    {selectedCategoryOption && (
                      <TableRow>
                        <TableCell sx={{ width: "30%" }}>SubCategory</TableCell>
                        <TableCell sx={{ width: "70%" }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Button
                              variant="text"
                              onClick={(e) => handleMenuOpen(e, "subCategory")}
                              removeUpperCase={true}
                              fullWidth
                              disabled={!subCategoryListOptions || subCategoryListOptions.length === 0}
                            >
                              {selectedSubCategoryOption || "-"}
                            </Button>
                            {selectedSubCategoryOption && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedSubCategoryOption(null);
                                  setValue('subCategory', null);
                                }}
                                sx={{ ml: 1 }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          <Menu
                            anchorEl={menuAnchor}
                            open={
                              Boolean(menuAnchor) && activeRow === "subCategory"
                            }
                            onClose={handleMenuClose}
                          >
                            {subCategoryListOptions?.map((option: { value: string; label: string }) => (
                              <MenuItem
                                key={option?.value}
                                onClick={() =>
                                  handleOptionSelect("subCategory", option)
                                }
                              >
                                <ListItemIcon>
                                  <FolderIcon />
                                </ListItemIcon>
                                <ListItemText primary={option?.label} />
                              </MenuItem>
                            ))}
                          </Menu>
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow>
                      <TableCell sx={{ width: "30%" }}>Publish</TableCell>
                      <TableCell sx={{ width: "70%" }} align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Typography sx={{ marginRight: 1 }}>NO</Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                color="success"
                                checked={checkPublish}
                                onChange={(e) => {
                                  const newValue = e.target.checked;
                                  setValue('publish', newValue);
                                  setCheckPublish(newValue);
                                }}
                              />
                            }
                            label=""
                            sx={{ margin: 0 }}
                          />
                          <Typography sx={{ marginLeft: 1 }}>YES</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={footerSx}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, width: '100%' }}>
          <Button
            type="submit"
            form="document-upload-form"
            variant="contained"
            startIcon={<FileUploadOutlined />}
            sx={primaryBtnSx}
          >
            Upload
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<Cancel />}
            onClick={onClose}
            sx={cancelBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentUploadPopup;
